import { Separator } from '@nirmata/web-components/ui/separator';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useIsMobile } from '@/hooks/useBreakpoint';
import { cn } from '@/utils/cn';

export interface SplitViewProps {
  left: React.ReactNode;
  right: React.ReactNode;
  initialLeftWidth?: number;
  minLeftWidth?: number;
  maxLeftWidth?: number;
  resizable?: boolean;
  orientation?: 'horizontal' | 'vertical';
  onResize?: (newWidth: number) => void;
  className?: string;
  leftPanelClassName?: string;
  rightPanelClassName?: string;
  dividerClassName?: string;
}

export function SplitView({
  left,
  right,
  initialLeftWidth = 300,
  minLeftWidth = 200,
  maxLeftWidth = 500,
  resizable = true,
  orientation = 'vertical',
  onResize,
  className = '',
  leftPanelClassName,
  rightPanelClassName,
  dividerClassName,
}: SplitViewProps) {
  const isMobile = useIsMobile();
  const [leftWidth, setLeftWidth] = useState(initialLeftWidth);
  const [isResizing, setIsResizing] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [activeSide, setActiveSide] = useState<'left' | 'right' | null>(null);

  // Mobile view uses a stacked approach with tab-like navigation
  const isVertical = orientation === 'vertical';
  const showBothPanels = !isMobile || !activeSide;

  // Toggle between panels on mobile
  const togglePanel = (side: 'left' | 'right') => {
    if (isMobile) {
      setActiveSide(side === activeSide ? null : side);
    }
  };

  // Handle resize logic
  const startResize = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (resizable && !isMobile) {
        setIsResizing(true);
        document.body.style.userSelect = 'none';
        document.body.style.cursor = isVertical ? 'col-resize' : 'row-resize';
      }
    },
    [resizable, isMobile, isVertical]
  );

  const handleResize = useCallback(
    (e: MouseEvent) => {
      if (isResizing && containerRef.current) {
        e.preventDefault();
        e.stopPropagation();
        const containerRect = containerRef.current.getBoundingClientRect();
        let newWidth;

        if (isVertical) {
          newWidth = Math.round(e.clientX - containerRect.left);
        } else {
          newWidth = Math.round(e.clientY - containerRect.top);
        }

        // Apply constraints
        newWidth = Math.max(minLeftWidth, Math.min(maxLeftWidth, newWidth));

        requestAnimationFrame(() => {
          setLeftWidth(newWidth);
          onResize?.(newWidth);
        });
      }
    },
    [isResizing, minLeftWidth, maxLeftWidth, onResize, isVertical]
  );

  const stopResize = useCallback(() => {
    setIsResizing(false);
    document.body.style.userSelect = '';
    document.body.style.cursor = '';
  }, []);

  // Event listeners for resize
  useEffect(() => {
    if (isResizing) {
      window.addEventListener('mousemove', handleResize, { passive: false });
      window.addEventListener('mouseup', stopResize);
      window.addEventListener('mouseleave', stopResize);

      return () => {
        window.removeEventListener('mousemove', handleResize);
        window.removeEventListener('mouseup', stopResize);
        window.removeEventListener('mouseleave', stopResize);
        document.body.style.userSelect = '';
        document.body.style.cursor = '';
      };
    }
  }, [isResizing, handleResize, stopResize]);

  return (
    <div
      ref={containerRef}
      className={cn(
        'relative flex',
        isVertical ? 'flex-row' : 'flex-col',
        'h-full',
        isResizing && 'select-none',
        className
      )}
      style={{ height: '100%' }}
    >
      {/* Mobile Tab Navigation */}
      {isMobile && (
        <div className="flex w-full h-10 border-b border-[var(--border)]">
          <button
            onClick={() => togglePanel('left')}
            className={`flex-1 flex items-center justify-center ${
              activeSide === 'left'
                ? 'bg-[var(--primary)] text-[var(--primary-foreground)]'
                : 'bg-[var(--popover)]'
            }`}
          >
            Panel 1
          </button>
          <button
            onClick={() => togglePanel('right')}
            className={`flex-1 flex items-center justify-center ${
              activeSide === 'right'
                ? 'bg-[var(--primary)] text-[var(--primary-foreground)]'
                : 'bg-[var(--popover)]'
            }`}
          >
            Panel 2
          </button>
        </div>
      )}

      {/* Left Panel */}
      {(showBothPanels || activeSide === 'left') && (
        <div
          className={cn('h-full overflow-auto flex-shrink-0', leftPanelClassName)}
          style={{
            [isVertical ? 'width' : 'height']: isMobile ? '100%' : `${leftWidth}px`,
            flexShrink: 0,
            height: '100%',
            maxWidth: `${maxLeftWidth}px`,
            minWidth: `${minLeftWidth}px`,
          }}
        >
          {left}
        </div>
      )}

      {/* Resizer */}
      {!isMobile && resizable && (
        <div
          onMouseDown={startResize}
          className={cn(
            'relative z-10 flex items-center justify-center bg-[var(--background)]',
            isVertical ? 'w-2 cursor-col-resize' : 'h-2 cursor-row-resize',
            'flex-shrink-0 transition-colors hover:bg-[var(--popover)]',
            isResizing && 'is-resizing bg-[var(--primary)]/20',
            dividerClassName
          )}
          style={{
            cursor: isVertical ? 'col-resize' : 'row-resize',
            height: '100%',
          }}
        >
          <Separator
            orientation={isVertical ? 'vertical' : 'horizontal'}
            className={cn(
              isResizing ? 'bg-[var(--primary)]/40' : 'bg-[var(--border)]',
              isVertical ? 'h-full' : 'w-full'
            )}
          />
        </div>
      )}

      {/* Right Panel */}
      {(showBothPanels || activeSide === 'right') && (
        <div
          className={cn('flex-1 h-full overflow-auto min-w-0', rightPanelClassName)}
          style={{
            height: '100%',
            minWidth: '400px', // Ensure right panel has a minimum width
          }}
        >
          {right}
        </div>
      )}
    </div>
  );
}
